import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TableService } from './table.service';

const TRANSACTIONS_PATH = '/transactions';
const AUTH_PATH = '/auth';

@Injectable({
  providedIn: 'root',
})
/* The filters live in a root scoped TableService, so they outlive the transactions
module. This service clears them when they stop being meaningful: leaving the
section, and ending the session. */
export class FiltersResetService {
  private isListening = false;

  constructor(
    private readonly router: Router,
    private readonly tableSvc: TableService
  ) {}

  /**
   * It starts watching the navigation to clear the transaction filters when they
   * stop applying. It is safe to call more than once, only the first call listens
   */
  listenToNavigation(): void {
    if (this.isListening) return;
    this.isListening = true;

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart
        )
      )
      .subscribe((event) => {
        if (this.shouldReset(event.url)) {
          this.tableSvc.reset();
        }
      });
  }

  /**
   * The filters are cleared when the user leaves the transactions section, or
   * whenever the session ends, wherever the user was standing
   * @param {string} targetUrl - the url the router is navigating to
   * @returns Whether the active filters stopped applying
   */
  private shouldReset(targetUrl: string): boolean {
    if (this.isPath(targetUrl, AUTH_PATH)) return true;

    return (
      this.isPath(this.router.url, TRANSACTIONS_PATH) &&
      !this.isPath(targetUrl, TRANSACTIONS_PATH)
    );
  }

  /**
   * It compares urls by path, ignoring query params and fragments, and treats the
   * children of a path as part of it, so a transaction detail is still inside the
   * transactions section
   * @param {string} url - the url to check
   * @param {string} path - the path to compare it against
   * @returns Whether the url belongs to the given path
   */
  private isPath(url: string, path: string): boolean {
    const urlPath = url.split('?')[0].split('#')[0];

    return urlPath === path || urlPath.startsWith(`${path}/`);
  }
}
